
import React, { useState, useMemo, useEffect, useRef } from "react";
import { FiEdit, FiEye, FiTrash2 } from "react-icons/fi";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ViewRiskModal from "../../components/head-of-Operations/ViewRiskModal.jsx";

const RiskTable = ({ risks, onEdit, onView, onDelete }) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [viewRisk, setViewRisk] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const itemsPerPage = 10;

  // Filter + search
  const filteredRisks = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [...risks];
    return risks.filter((r) => {
      const searchable = `${r.title} ${r.description} ${r.category} ${r.likelihood} ${r.impact} ${r.mitigation} ${r.contingencyPlan} ${r.riskOwner} ${r.status}`.toLowerCase();
      return searchable.includes(q);
    });
  }, [risks, search]);

  useEffect(() => setCurrentPage(1), [search, risks]);

  // Pagination
  const paginatedRisks = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRisks.slice(start, start + itemsPerPage);
  }, [filteredRisks, currentPage]);
  const totalPages = Math.ceil(filteredRisks.length / itemsPerPage);

  // Status color
  const getStatusColor = (status) => {
    switch (status) {
      case "Open":
        return "bg-red-100 text-red-700";
      case "In Progress":
        return "bg-yellow-100 text-yellow-700";
      case "Closed":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };
  // 🎨 Likelihood/Impact color
  const getLevelColor = (value) => {
    switch (value) {
      case "High":
        return "text-red-600";
      case "Medium":
        return "text-yellow-600";
      case "Low":
        return "text-green-600";
      default:
        return "text-gray-600";
    }
  };

  // Export helpers
  const mapRiskToExportRow = (r) => ({
    Title: r.title,
    Description: r.description,
    Category: r.category,
    Causes: r.causes,
    Consequences: r.consequences,
    Likelihood: r.likelihood,
    Impact: r.impact,
    Rating: r.rating,
    Mitigation: r.mitigation,
    Contingency: r.contingencyPlan,
    Owner: r.riskOwner,
    Comments: r.comments,
    Status: r.status,
    Treatments: r.treatments
      ? r.treatments.map((t) => `${t.action} (Due: ${t.dueDate}, Status: ${t.status})`).join("; ")
      :  "—",
  });

  const exportToExcel = () => {
    if (!filteredRisks.length) return alert("No risks to export!");
    const data = filteredRisks.map(mapRiskToExportRow);
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Risks");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      "risks_export.xlsx"
    );
  };

  const exportToCSV = () => {
    if (!filteredRisks.length) return alert("No risks to export!");
    const data = filteredRisks.map(mapRiskToExportRow);
    const worksheet = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "risks_export.csv");
  };

  const exportToPDF = () => {
    if (!filteredRisks.length) return alert("No risks to export!");
    const data = filteredRisks.map(mapRiskToExportRow);
    const doc = new jsPDF("l", "pt", "a4");
    const columns = Object.keys(data[0]);
    doc.setFontSize(18);
    doc.setTextColor(237, 125, 49);
    doc.text("Risks Export", 40, 40);
    autoTable(doc, {
      startY: 60,
      head: [columns],
      body: data.map((row) => columns.map((col) => row[col])),
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [237, 125, 49] },
      theme: "striped",
      margin: { left: 20, right: 20 },
    });
    doc.save("risks_export.pdf");
  };

  const handleExport = (format) => {
    setExportDropdownOpen(false);
    if (format === "excel") exportToExcel();
    else if (format === "csv") exportToCSV();
    else if (format === "pdf") exportToPDF();
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="mt-6 bg-white rounded shadow p-4">
  {/* Search + Export */}
  <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
    <input
      type="search"
      placeholder="Search risks..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      className="w-full sm:max-w-xs rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
    />

    {/* Export Dropdown */}
    <div className="relative inline-block text-left w-full sm:w-auto" ref={dropdownRef}>
      <button
        onClick={() => setExportDropdownOpen((o) => !o)}
        className="w-full sm:w-auto inline-flex justify-center rounded bg-orange-600 px-4 py-2 text-sm text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
      >
        Export
        <svg className="ml-2 -mr-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {exportDropdownOpen && (
        <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1">
            {["excel", "csv", "pdf"].map((format) => (
              <button
                key={format}
                onClick={() => handleExport(format)}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900"
              >
                Export as {format.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>

  {/* Table */}
  <div className="overflow-x-auto -mx-2 sm:mx-0">
    <table className="min-w-full table-auto text-sm text-gray-700">
      <thead>
        <tr className="bg-orange-100 text-gray-600 text-left uppercase text-xs">
          <th className="px-2 sm:px-4 py-2 text-orange-600">Title</th>
          <th className="px-2 sm:px-4 py-2 text-orange-600 hidden sm:table-cell">Category</th>
          <th className="px-2 sm:px-4 py-2 text-orange-600">Likelihood</th>
          <th className="px-2 sm:px-4 py-2 text-orange-600 hidden md:table-cell">Impact</th>
          <th className="px-2 sm:px-4 py-2 text-orange-600 hidden md:table-cell">Rating</th>
          <th className="px-2 sm:px-4 py-2 text-orange-600">Status</th>
          <th className="px-2 sm:px-4 py-2 text-orange-600 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {paginatedRisks.length === 0 ? (
          <tr>
            <td colSpan="7" className="text-center text-gray-500 py-4">
              No risks found.
            </td>
          </tr>
        ) : (
          paginatedRisks.map((risk, index) => (
            <tr key={index} className="border-b hover:bg-gray-50">
              <td className="px-2 sm:px-4 py-2 font-medium break-words max-w-[120px] sm:max-w-[200px] truncate">
                {risk.title}
              </td>
              <td className="px-2 sm:px-4 py-2 hidden sm:table-cell">{risk.category}</td>
              <td className={`px-2 sm:px-4 py-2 font-semibold ${risk.likelihood >= 4 ? "text-red-600" : risk.likelihood >= 2 ? "text-yellow-600" : "text-green-600"}`}>
                {risk.likelihood}
              </td>
              <td className={`px-2 sm:px-4 py-2 font-semibold hidden md:table-cell ${risk.impact >= 4 ? "text-red-600" : risk.impact >= 2 ? "text-yellow-600" : "text-green-600"}`}>
                {risk.impact}
              </td>
              <td className={`px-2 sm:px-4 py-2 font-semibold hidden md:table-cell ${getLevelColor(risk.rating)}`}>
                {risk.rating}
              </td>
              <td>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(risk.status)}`}>
                  {risk.status}
                </span>
              </td>
              <td className="px-2 sm:px-4 py-2 flex justify-end gap-2 sm:gap-3">
                <button onClick={() => onView(risk)} className="text-orange-600 hover:text-orange-900">
                  <FiEye />
                </button>
                <button onClick={() => onEdit(risk)} className="text-orange-600 hover:text-orange-900">
                  <FiEdit />
                </button>
                <button onClick={() => onDelete(risk._id)} className="text-red-600 hover:text-red-800">
                  <FiTrash2 />
                </button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>

  {/* Pagination */}
  {totalPages > 1 && (
    <div className="mt-4 flex flex-wrap justify-center gap-2">
      <button
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        className={`rounded px-3 py-1 text-sm ${currentPage === 1 ? "cursor-not-allowed bg-gray-300" : "bg-orange-100 text-orange-600 hover:bg-orange-200"}`}
      >
        Prev
      </button>
      {[...Array(totalPages)].map((_, i) => (
        <button
          key={i}
          onClick={() => setCurrentPage(i + 1)}
          className={`rounded px-3 py-1 text-sm ${currentPage === i + 1 ? "bg-orange-300 text-orange-900 font-semibold" : "bg-gray-200 hover:bg-gray-300"}`}
        >
          {i + 1}
        </button>
      ))}
      <button
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
        className={`rounded px-3 py-1 text-sm ${currentPage === totalPages ? "cursor-not-allowed bg-gray-300" : "bg-orange-100 text-orange-600 hover:bg-orange-200"}`}
      >
        Next
      </button>
    </div>
  )}

       {/* View Modal */}
      <ViewRiskModal isOpen={viewModalOpen} onClose={() => setViewModalOpen(false)} risk={viewRisk} />
    </div>
  );
};

export default RiskTable;
